/** Which section cards the editor shows: the saved sections in their order, then the kind's suggestions not yet written. */

import type { SectionOut, TemplateSectionOut } from '@/api/types';

export interface PlannedSection {
  key: string;
  title: string;
  state: 'suggested' | 'filled' | 'custom';
  /** The saved section, when there is one. */
  section: SectionOut | null;
  /** The template creates it internal. */
  internal: boolean;
}

export function planSections(
  sections: SectionOut[],
  template: TemplateSectionOut[],
  canInternal: boolean,
): { saved: PlannedSection[]; suggested: PlannedSection[] } {
  const internalKeys = new Set(template.filter((t) => t.internal).map((t) => t.key));
  const saved = [...sections]
    .sort((a, b) => a.position - b.position)
    .map((s): PlannedSection => ({
      key: s.key,
      title: s.title,
      state: s.is_custom ? 'custom' : 'filled',
      section: s,
      internal: internalKeys.has(s.key),
    }));
  const have = new Set(sections.map((s) => s.key));
  const suggested = [...template]
    .sort((a, b) => a.position - b.position)
    .filter((t) => !have.has(t.key) && (canInternal || !t.internal))
    .map((t): PlannedSection => ({ key: t.key, title: t.title, state: 'suggested', section: null, internal: t.internal }));
  return { saved, suggested };
}

/** A custom section's key from its title (`custom-<slug>`), unique among `taken`. */
export function customKey(title: string, taken: Iterable<string>): string {
  const slug =
    title
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60)
      .replace(/-+$/g, '') || 'section';
  const used = new Set(taken);
  let key = `custom-${slug}`;
  for (let n = 2; used.has(key); n += 1) key = `custom-${slug}-${n}`;
  return key;
}

/** `keys` with the one at `index` moved by `delta` (an out-of-range move changes nothing). */
export function moveKey(keys: string[], index: number, delta: -1 | 1): string[] {
  const target = index + delta;
  if (index < 0 || index >= keys.length || target < 0 || target >= keys.length) return keys;
  const next = [...keys];
  [next[index], next[target]] = [next[target] as string, next[index] as string];
  return next;
}
